import { useContext } from 'react'
import { Configuration, OpenAIApi } from 'openai'

/* Context ====================================================================================== */
import { NavigationContext } from '../../../contexts/navigation'

/* Core ========================================================================================= */
import { addSnippet } from '../../../core/snippet-manager'

/* Hooks ======================================================================================== */
import useValue from '../../../hooks/use-value'

/* Components =================================================================================== */
import { Snippet, SnippetHead, SnippetBody } from '../../../modules/layouts/snippet'
import { Textarea } from '../../../components/textarea'
import { Form } from '../../../components/form'
import { Button } from '../../../components/button'
import { Box } from '../../../components/box/box.component'
import { FIELDS } from '../../../share/fields'
import { Modal } from '../../../components/modal'
import { Loading } from '../../../components/loading'
import { Line } from '../../../components/line'
import '../../modals/gpt-edit'

/* Styles ======================================================================================= */
import { mt8 } from '../../../styles/styleset/margin/mt8'
import { flat } from '../../../share/obj-equal'
import { formatBool } from '../../../share/format'

const configuration = new Configuration({
  apiKey: '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',
})
const openai = new OpenAIApi(configuration)

export default addSnippet('GPT', (props: any) => {
  const { row } = props
  const { navigation } = useContext(NavigationContext)
  const [isLoading, $isLoading] = useValue(false)
  const [instruction, $instruction] = useValue('')
  const [loaded, $loaded] = useValue([false, false, false, false, false])

  const emptyValues: any = {}
  const emptyValuesLabels: any = {}

  function getEmptyValues(fields: any, parentKey?: any, parentLabel?: any) {
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i]
      const key = `${parentKey ? `${parentKey}` : ''}${field.name ? `.${field.name}` : '' || ''}`
      const label = `${parentLabel ? `${parentLabel}` : ''}${
        field.label ? ` / ${field.label}` : '' || ''
      }`

      if (field.fields) {
        getEmptyValues(field.fields, key, label)
      }
      if (!field.name) continue

      switch (field.type) {
        case FIELDS.GROUP:
          continue
        case FIELDS.CHECKBOX:
          emptyValues[key] = false
          emptyValuesLabels[key] = label
          continue
        default:
          emptyValues[key] = ''
          emptyValuesLabels[key] = label
      }
    }
  }

  if (navigation.payload.base.form.sections) {
    navigation.payload.base.form.sections.map(section => {
      getEmptyValues(section.fields, section.name, section.label)
    })
  } else {
    getEmptyValues(navigation.payload.base.form.fields)
  }

  function mergeEmptyValues() {
    return {
      ...emptyValues,
      ...flat(row.body),
    }
  }

  function divide(values) {
    const keys = Object.keys(values).filter(
      a => !a.includes('createdAt') || !a.includes('createdBy') || a !== 'id',
    )
    const output = [{}, {}, {}, {}, {}, {}]

    for (let i = 0; i < keys.length; i++) {
      output[i % (output.length - 1)][keys[i]] = values[keys[i]]
    }

    return output
  }

  return (
    <Snippet>
      <SnippetHead
        title={
          <Box flex alignCenterY>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="671.194"
              height="680.2487"
              viewBox="0 0 671.194 680.2487"
              style={{
                width: 20,
                height: 20,
                marginRight: 8,
              }}
            >
              <path d="M626.9464,278.4037a169.4492,169.4492,0,0,0-14.5642-139.187A171.3828,171.3828,0,0,0,427.7883,56.9841,169.45,169.45,0,0,0,299.9746.0034,171.3985,171.3985,0,0,0,136.4751,118.6719,169.5077,169.5077,0,0,0,23.1574,200.8775,171.41,171.41,0,0,0,44.2385,401.845,169.4564,169.4564,0,0,0,58.8021,541.0325a171.4,171.4,0,0,0,184.5945,82.2318A169.4474,169.4474,0,0,0,371.21,680.2454,171.4,171.4,0,0,0,534.7642,561.51a169.504,169.504,0,0,0,113.3175-82.2063,171.4116,171.4116,0,0,0-21.1353-200.9ZM371.2647,635.7758a127.1077,127.1077,0,0,1-81.6027-29.5024c1.0323-.5629,2.8435-1.556,4.0237-2.2788L429.13,525.7575a22.0226,22.0226,0,0,0,11.1306-19.27V315.5368l57.25,33.0567a2.0332,2.0332,0,0,1,1.1122,1.568V508.2972A127.64,127.64,0,0,1,371.2647,635.7758ZM97.3705,518.7985a127.0536,127.0536,0,0,1-15.2074-85.4256c1.0057.6037,2.7624,1.6768,4.0231,2.4012L221.63,514.01a22.04,22.04,0,0,0,22.2492,0L409.243,418.5281v66.1134a2.0529,2.0529,0,0,1-.818,1.7568l-136.92,79.0534a127.6145,127.6145,0,0,1-174.134-46.6532ZM61.7391,223.1114a127.0146,127.0146,0,0,1,66.3545-55.8944c0,1.1667-.067,3.2329-.067,4.6665V328.3561a22.0038,22.0038,0,0,0,11.1173,19.2578l165.3629,95.4695-57.2481,33.055a2.0549,2.0549,0,0,1-1.9319.1752l-136.933-79.1215A127.6139,127.6139,0,0,1,61.7391,223.1114ZM532.0959,332.5668,366.7308,237.0854l57.25-33.0431a2.0455,2.0455,0,0,1,1.93-.1735l136.934,79.0535a127.5047,127.5047,0,0,1-19.7,230.055V351.8247a21.9961,21.9961,0,0,0-11.0489-19.2579Zm56.9793-85.7589c-1.0051-.6174-2.7618-1.6769-4.0219-2.4L449.6072,166.1712a22.07,22.07,0,0,0-22.2475,0L261.9963,261.6543V195.5409a2.0529,2.0529,0,0,1,.818-1.7567l136.9205-78.988a127.4923,127.4923,0,0,1,189.34,132.0117ZM230.8716,364.6456,173.6082,331.589a2.0321,2.0321,0,0,1-1.1122-1.57V171.8835A127.4926,127.4926,0,0,1,381.5636,73.9884c-1.0322.5633-2.83,1.5558-4.0236,2.28L242.0957,154.5044a22.0025,22.0025,0,0,0-11.1306,19.2566Zm31.0975-67.0521L335.62,255.0559l73.6488,42.51v85.0481L335.62,425.1266l-73.6506-42.5122Z" />
            </svg>
            <span>AI Assistant</span>
          </Box>
        }
      />
      <Line />
      <SnippetBody>
        <Box className={mt8} relative>
          <Form
            onSubmit={async (values: any) => {
              if (!instruction) return

              $isLoading.set(true)
              const originalValue = mergeEmptyValues()
              const inputs = divide(originalValue)

              await Promise.allSettled(
                inputs.map((input, key) => {
                  return openai
                    .createEdit({
                      model: 'code-davinci-edit-001',
                      input: JSON.stringify(input),
                      instruction: `Only give me a valid JSON and return only updated value.\n${values.instruction}.`,
                      temperature: 0,
                      top_p: 0,
                    })
                    .then(payload => {
                      $loaded.set(prev => {
                        const newPrev = [...prev]
                        newPrev[key] = true

                        return newPrev
                      })

                      return payload
                    })
                    .catch(e => {
                      $loaded.set(prev => {
                        const newPrev = [...prev]
                        newPrev[key] = true

                        return newPrev
                      })
                      return e
                    })
                }),
              ).then((response: any) => {
                let output = {}

                response.map(res => {
                  const text = res?.value?.data?.choices[0]?.text
                  output = {
                    ...output,
                    ...JSON.parse(text || '{}'),
                  }
                })

                for (const key in output) {
                  if (typeof output[key] === 'boolean') {
                    output[key] = formatBool(output[key])

                    if (!originalValue[key] && !output[key]) delete output[key]
                  }
                  if (originalValue[key] === output[key]) {
                    delete output[key]
                  }
                }

                Modal.push({
                  type: 'GPTEdit',
                  componentProps: { values: output, labels: emptyValuesLabels },
                })

                $isLoading.set(false)
                $loaded.reset()
              })
            }}
            onChange={({ instruction }) => {
              $instruction.set(instruction)
            }}
          >
            <Textarea name="instruction" placeholder="Simply write a command here to edit data!" />
            <Box className={mt8} flex alignRight>
              <Button
                icon={isLoading && <Loading size="xs" />}
                label={isLoading ? 'Loading...' : 'Submit'}
                type="submit"
                size="sm"
                baseline="primary"
                disabled
                square
              />
            </Box>
          </Form>
        </Box>
      </SnippetBody>
      <Box
        style={{
          height: 4,
          width: `${(loaded.filter(a => a).length / loaded.length) * 100}%`,
          left: 0,
          bottom: 0,
          borderRadius:
            loaded.filter(a => a).length !== loaded.length ? '0 0 0 4px' : '0 0 4px 4px',
          transition: 'all .2s ease',
        }}
        baseline="secondary"
        absolute
      ></Box>
    </Snippet>
  )
})
